import React, { useEffect, useState } from "react";
import { TablePagination } from "@mui/material";
import DownloadIcon from '@mui/icons-material/Download';
import { useQueryParams, useDebounce } from "../../../hooks";
import { getOrganicJobsListapi } from "./Helper";

const JobMOrganicList = () => {
  const [jobs, setJobs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const { getParam, setMultiParam } = useQueryParams();
  const debouncedSearch = useDebounce(searchTerm, 1000);

  const currentPage = getParam("page");
  const currentLimit = getParam("pageSize");

  useEffect(() => {
    setPage(parseInt(currentPage) || 0);
    setPageSize(parseInt(currentLimit) || 10);
  }, [currentPage, currentLimit]);

  const fetchJobs = async (search = searchTerm, currentPage = page + 1, rowsPerPage = pageSize) => {
    try {
      const response = await getOrganicJobsListapi(currentPage, rowsPerPage, search);
      setJobs(response.data.data || []);
      setTotalCount(response.data.total || 0);
    } catch (error) {
      console.error("Error fetching jobs:", error);
      setJobs([]);
      setTotalCount(0);
    }
  };

  useEffect(() => {
    fetchJobs(debouncedSearch, page + 1, pageSize);
  }, [page, pageSize, debouncedSearch]);

  useEffect(() => {
    setMultiParam({ page, pageSize });
  }, [page, pageSize]);

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    fetchJobs(searchTerm, newPage + 1, pageSize);
  };

  const handleChangeRowsPerPage = (event) => {
    const newPageSize = parseInt(event.target.value, 10);
    setPageSize(newPageSize);
    setPage(0);
    fetchJobs(searchTerm, 1, newPageSize);
  };

  const getItemIndex = (index) => page * pageSize + index + 1;

  const handleDownloadCandidates = async (jobId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_MARKETPLACE_URL_NEW}api/job-application/download/${jobId}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("ProlegionAccessToken")}`,
          },
        }
      );

      if (!response.ok) throw new Error("Failed to download candidates");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `candidates_${jobId}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading candidates:", error);
    }
  };

  return (
    <div className="p-5 shadow-md bg-white">
      <div className="flex justify-between">
        <div className="text-lg uppercase tracking-wider font-bold text-[#2EBAA3]">
          <h3>Organic Jobs</h3>
        </div>
        <input
          type="text"
          id="search"
          placeholder="Search"
          value={searchTerm}
          onChange={handleSearch}
          className="px-3 py-1.5 mr-2 mb-2 form-control block w-[150px] text-base font-normal text-gray-700 bg-white bg-clip-padding border border-solid border-gray-300 rounded transition ease-in-out"
        />
      </div>

      <br />

      <div className="overflow-x-auto relative">
        <table className="w-full text-sm text-left text-gray-500">
          <thead className="text-xs text-gray-700 uppercase bg-gray-50">
            <tr>
              <th className="py-3 px-3">ID</th>
              <th className="py-3 px-6">Job Name</th>
              <th className="py-3 px-6">Company Name</th>
              <th className="py-3 px-6">User Type</th>
              <th className="py-3 px-6">Salary</th>
              <th className="py-3 px-6">Number of Position</th>
              <th className="py-3 px-6 text-center">All Applicants</th>
              <th scope="col" className="py-3 px-6 whitespace-nowrap w-[150px]">
                <div>Reviewed</div>
              </th>
              <th scope="col" className="py-3 px-6 whitespace-nowrap w-[150px]">
                <div>Interview</div>
                <div>Scheduled</div>
              </th>
              <th scope="col" className="py-3 px-6 whitespace-nowrap w-[150px]">Job Offered</th>
              <th scope="col" className="py-3 px-6 whitespace-nowrap w-[150px]">Hired</th>
              <th scope="col" className="py-3 px-6 whitespace-nowrap border-r-2 border-gray-300 w-[150px]">Rejected</th>
              <th scope="col" className="py-3 px-6 whitespace-nowrap w-[150px]">Recommended</th>
              <th scope="col" className="py-3 px-6 whitespace-nowrap w-[150px]">Good Fit</th>
              <th scope="col" className="py-3 px-6 whitespace-nowrap w-[150px]">Average</th>
              <th scope="col" className="py-3 px-6 whitespace-nowrap w-[150px]">Below Average</th>
              <th className="py-3 px-6">Download Data</th>
            </tr>
          </thead>
          <tbody>
            {jobs && jobs.length > 0 ? (
              jobs.map((job, index) => (
                <tr className="bg-white border-b" key={index}>
                  <th className="py-4 px-6">{getItemIndex(index)}</th>
                  <td className="py-4 px-6">{job.name}</td>
                  <td className="py-4 px-6">{job.vendorName}</td>
                  <td className="py-4 px-6">{job.role}</td>
                  <td className="py-4 px-2">
                  {job.salary && (job.salary.min || job.salary.max) ? (
                    <>
                      {job.salary.currency === "INR" ? "₹" : job.salary.currency === "USD" ? "$" : ""}
                      {job.salary.min && job.salary.max ? (
                        <>
                          {job.salary.min < 1000 ? job.salary.min : `${(job.salary.min / 1000).toFixed(0)}k`} -{" "}
                          {job.salary.max < 1000 ? job.salary.max : `${(job.salary.max / 1000).toFixed(0)}k`}
                        </>
                      ) : (
                        <>
                          {job.salary.max && job.salary.max < 1000
                            ? job.salary.max
                            : job.salary.min && job.salary.min < 1000
                            ? job.salary.min
                            : `${(job.salary.max || job.salary.min) / 1000}k`}
                        </>
                      )}
                      {job.salary.type ? ` / ${job.salary.type === "month" ? "mth" : job.salary.type === "year" ? "yr" : job.salary.type}` : ""}
                    </>
                  ) : (
                    "--"
                  )}
                  </td>
                  <td className="py-4 px-6">{job.number_of_position || 0}</td>
                  <td className="py-4 px-6">{job.applicant_count || 0}</td>
                  <td className="py-4 px-12">{job.applicationCounts?.screening || 0}</td>
                  <td className="py-4 px-12">{job.applicationCounts?.interview_scheduled || 0}</td>
                  <td className="py-4 px-12">{job.applicationCounts?.job_offered || 0}</td>
                  <td className="py-4 px-8">{job.applicationCounts?.hired || 0}</td>
                  <td className="py-4 px-12 border-r-2 border-gray-300">{job.applicationCounts?.reject || 0}</td>
                  <td className="py-4 px-12">{job?.applicationStrengthCounts?.Recommended || 0}</td>
                  <td className="py-4 px-12">{job?.applicationStrengthCounts?.["Good Fit"] || 0}</td>
                  <td className="py-4 px-12">{job?.applicationStrengthCounts?.Average || 0}</td>
                  <td className="py-4 px-12">{job?.applicationStrengthCounts?.["Below Average"] || 0}</td>
                  <td className="py-4 px-6">
                    <button
                      onClick={() => handleDownloadCandidates(job.id)}
                      className="flex items-center gap-1 text-blue-500 hover:underline"
                    >
                      <DownloadIcon fontSize="small" />
                      Download
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={19} className="py-10 px-6 text-center text-lg font-semibold text-gray-500">
                  No data found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={pageSize}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[2, 5, 10, 25, 50, 100]}
      />
    </div>
  );
};

export default JobMOrganicList;
